import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentTOSVersion } from '../utils/termsVersion';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const useTermsOfService = () => {
  const { user, token, refreshUserData, isLoading, showOnboarding } = useAuth();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isCheckingTerms, setIsCheckingTerms] = useState(true);
  const hasCheckedInitialTerms = useRef(false);
  const location = useLocation();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!isLoading && user && token) {
      setIsCheckingTerms(true);
      setShowTermsModal(false);

      timeoutId = setTimeout(() => {
        const termsFieldsPresent = user.termsAcceptedAt !== undefined && user.termsVersion !== undefined;

        if (termsFieldsPresent) {
          const currentTosVersion = getCurrentTOSVersion();
          const hasAcceptedLatestTerms =
            !!user.termsAcceptedAt &&
            user.termsVersion === currentTosVersion;

          const isLegalPage =
            location.pathname === '/legal/terms-of-service' ||
            location.pathname === '/legal/privacy-policy';

          // Don't show TOS modal if onboarding is active
          if (!hasAcceptedLatestTerms && !isLegalPage && !showOnboarding) {
            setShowTermsModal(true);
          } else {
            setShowTermsModal(false);
          }
        }
        
        hasCheckedInitialTerms.current = true;
        setIsCheckingTerms(false);
      }, 500);
    } else {
      setShowTermsModal(false);
      hasCheckedInitialTerms.current = false;
      
      if (isLoading) {
        setIsCheckingTerms(true);
      } else {
        setIsCheckingTerms(false);
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user, token, isLoading, location.pathname, showOnboarding]);

  const acceptTerms = useCallback(async () => {
    if (!user || !token) return;
    
    setIsAccepting(true);
    try {
      await axios.post(
        `${API_URL}/api/user/accept-terms`,
        { termsVersion: getCurrentTOSVersion() },
        { withCredentials: true } // Use cookies instead of Authorization header
      );
      
      await refreshUserData();
      setShowTermsModal(false);
    } catch (error) {
      logger.error('Error accepting terms:', error);
    } finally {
      setIsAccepting(false);
    }
  }, [user, token, refreshUserData]);
  
  // Return the raw state values and let the consumer decide when to show
  return {
    showTermsModal,
    acceptTerms,
    isAccepting,
    isCheckingTerms,
    hasCheckedInitialTerms: hasCheckedInitialTerms.current
  };
}; 